import { create } from "zustand";
import api from "../../utils/api";
import { getError } from "../../utils/getError";
import type { CustomAlertProps } from "../../pages/oasis/components/CustomAlert";


export interface GenerateHistoryReportData {
  deviceIds: string[];
  startDate: string;
  endDate: string;
  typeReport: string;
  typeDocument: "excel" | "pdf" | "json";
  withGeocoding?: boolean;
  minSpeed?: string;
  ralenti?: string;
  maxSpeed?: string;
  rpm?: string;
  tempScale?: "celsius" | "fahrenheit";
  highLimit?: string;
  lowLimit?: string;
  geofences?: string[];
  timeZone?: string;
}

type Report = {
  reportId: string;
  typeReport: string;
  typeDocument: "excel" | "pdf";
  status: "pending" | "processing" | "completed" | "failed" | "readed";
  fileName: string;
  fileUrl: string | null;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt?: { _seconds: number; _nanoseconds: number };
  expiresAt: { _seconds: number; _nanoseconds: number };
}

// Tipo para respuesta de reporte en background
type BackgroundReportResponse = {
  success: boolean;
  message: string;
  data: {
    reportId: string;
    status: "pending";
  };
}

const POLLING_INITIAL_MS = 15_000;
const POLLING_SLOW_MS = 30_000;
const POLLING_SLOW_AFTER = 8;
// Si un job lleva más de este tiempo sin actualizarse, lo consideramos zombi
// y no lo mostramos como pendiente. Debe ser >= STALE_REPORT_MINUTES del
// backend (15 min) para no rebotar antes de que el watchdog lo recoja.
export const STALE_REPORT_MS = 15 * 60 * 1000;

const getReportUpdatedMs = (r: { updatedAt?: { _seconds: number }; createdAt?: { _seconds: number } }): number => {
  const ts = r.updatedAt ?? r.createdAt;
  return ts ? ts._seconds * 1000 : 0;
};

const isFreshPending = (r: { status: string; updatedAt?: { _seconds: number }; createdAt?: { _seconds: number } }): boolean => {
  if (r.status !== "pending" && r.status !== "processing") return false;
  const updatedMs = getReportUpdatedMs(r);
  if (updatedMs === 0) return true; // sin timestamp, asumir fresco (recién creado)
  return Date.now() - updatedMs < STALE_REPORT_MS;
};

// Extrae el filename del header Content-Disposition. Soporta:
//   attachment; filename="historic_2026-01-01_2026-01-31.xlsx"
//   attachment; filename=historic_2026-01-01_2026-01-31.xlsx
//   attachment; filename*=UTF-8''historic_2026-01-01_2026-01-31.xlsx
const parseFilenameFromDisposition = (disposition: string | undefined | null): string | null => {
  if (!disposition) return null;
  const utf8Match = /filename\*=(?:UTF-8|utf-8)''([^;]+)/i.exec(disposition);
  if (utf8Match?.[1]) {
    try { return decodeURIComponent(utf8Match[1].trim()); } catch { /* fallthrough */ }
  }
  const quotedMatch = /filename="([^"]+)"/i.exec(disposition);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();
  const bareMatch = /filename=([^;]+)/i.exec(disposition);
  if (bareMatch?.[1]) return bareMatch[1].trim();
  return null;
};

interface ReportsStore {
  loading: boolean;
  loadingDevices: boolean;
  error: string | null;
  reports: Report[];
  loadingReports: boolean;
  reportsRead: string[];
  pollingInterval: ReturnType<typeof setInterval> | null;
  pollingTick: number;
  generateHistoryReport: (
    reportData: GenerateHistoryReportData,
    showAlert: (config: CustomAlertProps) => void,
    onClose: () => void
  ) => Promise<{
    success: boolean;
    data?: Blob;
    fileName?: string;
    error?: string;
    isBackground?: boolean;
    reportId?: string;
  }>;
  getReportsOfUser: (showAlert?: (config: CustomAlertProps) => void) => Promise<Report[]>;
  getReportsOfUserSilent: () => Promise<Report[]>;
  setReportsRead: (reportsRead: string[]) => void;
  readedReport: (reportId: string) => Promise<void>;
  readAllReports: (userId: string) => Promise<void>;
  hasPendingReports: () => boolean;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

// ... (las funciones helper se mantienen igual)

export const useReportsStore = create<ReportsStore>((set, get) => ({
  loading: false,
  loadingDevices: false,
  loadingReports: false,
  error: null,
  reports: [],
  reportsRead: [],
  pollingInterval: null,
  pollingTick: 0,
  setReportsRead: (reportsRead: string[]) => set({ reportsRead }),

  hasPendingReports: () => {
    // Sólo cuenta reportes pendientes FRESCOS — ignora zombis para que el
    // polling pare cuando el watchdog del backend tarda en limpiar.
    const reports = get().reports;
    return reports.some(isFreshPending);
  },

  startPolling: (intervalMs = POLLING_INITIAL_MS) => {
    const existingInterval = get().pollingInterval;
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    set({ pollingTick: 0 });

    const scheduleTick = (ms: number) => {
      const interval = setInterval(() => {
        const hasPending = get().hasPendingReports();
        if (!hasPending) {
          get().stopPolling();
          return;
        }

        get().getReportsOfUserSilent();
        const tick = get().pollingTick + 1;
        set({ pollingTick: tick });

        if (tick === POLLING_SLOW_AFTER && ms < POLLING_SLOW_MS) {
          clearInterval(interval);
          scheduleTick(POLLING_SLOW_MS);
        }
      }, ms);

      set({ pollingInterval: interval });
    };

    scheduleTick(intervalMs);
  },

  stopPolling: () => {
    const interval = get().pollingInterval;
    if (interval) {
      clearInterval(interval);
      set({ pollingInterval: null, pollingTick: 0 });
    }
  },

  getReportsOfUserSilent: async (): Promise<Report[]> => {
    try {
      const { data } = await api.get("/reports/user");
      set({ reports: data.data });
      return data.data;
    } catch {
      return [];
    }
  },

  generateHistoryReport: async (
    reportData: GenerateHistoryReportData,
    showAlert: (config: CustomAlertProps) => void,
    onClose: () => void
  ) => {
    set({ loading: true, loadingDevices: true, error: null });
    try {
      const response = await api.get("/reports", {
        params: {...reportData, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        responseType: "blob",
        timeout: 120000,
      });

      // Verificar si es respuesta de background (202) o archivo directo
      const contentType = response.headers["content-type"];

      // Si es JSON, es un reporte en background
      if (contentType?.includes("application/json")) {
        // Convertir blob a JSON
        const text = await response.data.text();
        const jsonResponse: BackgroundReportResponse = JSON.parse(text);

        set({ loading: false, loadingDevices: false });

        // Mostrar alerta informativa
        showAlert({
          title: "Reporte en proceso",
          text: "Tu reporte se está generando en segundo plano. Te notificaremos cuando esté listo para descargar.",
          icon: "info",
          confirmButtonText: "Entendido",
          confirmButtonColor: "#2563eb",
          onConfirm: () => {
            onClose();
          },
          showCancelButton: false,
        });

        // Refrescar lista de reportes e iniciar polling
        await get().getReportsOfUser();
        get().startPolling();

        return {
          success: true,
          isBackground: true,
          reportId: jsonResponse.data.reportId
        };
      }

      // Es un archivo directo (blob). Tomamos el nombre del header del backend
      // (formato `${typeReport}_${startDate}_${endDate}.${ext}`) con fallback
      // genérico si CORS lo oculta o el header viene vacío.
      const extension = reportData.typeDocument === "excel" ? "xlsx" : "pdf";
      const disposition = response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
      const fileName =
        parseFilenameFromDisposition(disposition as string | undefined) ||
        `reporte-${reportData.typeReport}.${extension}`;

      set({ loading: false, loadingDevices: false });
      return { success: true, data: response.data, fileName, isBackground: false };

    } catch (err: any) {
      console.error("Error generating history report:", err);

      // Con responseType: "blob", los errores HTTP traen err.response.data como Blob.
      // Hay que convertirlo a JSON para que getError pueda leer el mensaje del backend.
      let errorMessage: string;
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          errorMessage = getError({ ...err, response: { ...err.response, data: parsed } });
        } catch {
          errorMessage = getError(err);
        }
      } else {
        errorMessage = getError(err);
      }

      set({ error: errorMessage, loading: false, loadingDevices: false });
      showAlert({
        title: "Error al generar el reporte",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {
          onClose();
        },
        showCancelButton: false,
      });
      return { success: false, error: errorMessage };
    }
  },

  getReportsOfUser: async (showAlert?: (config: CustomAlertProps) => void): Promise<Report[]> => {
    set({ loadingReports: true });
    try {
      const { data } = await api.get("/reports/user");
      set({ reports: data.data });
      return data.data;
    } catch {
      if (showAlert) {
        showAlert({
          title: "Error al obtener los reportes",
          text: "Error al obtener los reportes",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#2563eb",
          showCancelButton: false,
        });
      }
      return [];
    } finally {
      set({ loadingReports: false });
    }
  },

  readedReport: async (reportId: string): Promise<void> => {
    try {
      await api.put(`/reports/readed?reportId=${reportId}`);
      // Actualizar el estado local del reporte a "readed"
      const reports = get().reports.map(report =>
        report.reportId === reportId ? { ...report, status: "readed" as const } : report
      );
      set({ reports });
    } catch (error) {
      console.error("Error marking report as read:", error);
    }
  },

  readAllReports: async (userId: string): Promise<void> => {
    try {
      await api.put(`/reports/readed/all?userId=${userId}`);
      // Actualizar todos los reportes a "readed" localmente
      const reports = get().reports.map(report => ({ ...report, status: "readed" as const }));
      set({ reports });
    } catch (error) {
      console.error("Error marking all reports as read:", error);
    }
  },

}));